import { Box, Button, Header, Input, Modal, SpaceBetween } from "@amzn/awsui-components-react";
import { getString } from "common";
import { useEffect, useState } from "react";
import { useGetComments } from "hooks";
import { OrderItem } from "@amzn/ito-client";
import { debounce } from "lodash";
import { MessageList } from "../Messages/MessagesList";

const stringPath = "orderDetails.tabs.itemsRequested.lineCommentModal";
const FIVE_MINUTES_IN_MS = 300000;
const HALF_SECOND_IN_MS = 500;

export interface CommentsModalProps {
    items: OrderItem[];
    showModal: boolean;
    showComments: boolean;
    isPosting?: boolean;
    commentRefreshSignal?: number;
    onSubmit: (comment: string) => void | Promise<void>;
    onDismiss: () => void;
}

export const LineCommentModal = (props: CommentsModalProps) => {
    const { showModal, onDismiss } = props;
    const [inputComment, setInputComment] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { comments, isLoading: isLoadingComments, doGetComments } = useGetComments(undefined);

    const isLoading = isLoadingComments || !!props.isPosting || isSubmitting;

    // Auto refresh
    useEffect(() => {
        if (props.showComments == true) {
            const interval = setInterval(() => {
                doGetComments({
                    taxonomyId: props.items[0].taxonomyId!,
                    fromAutoRefresh: true,
                    filters: { },
                });
            }, FIVE_MINUTES_IN_MS);
            return () => clearInterval(interval);
        }
    }, []);

    useEffect(() => {
        if (props.showComments == true) {
            doGetComments({ taxonomyId: props.items[0].taxonomyId!, filters: {  } });
        }
    }, [props.showComments, props.commentRefreshSignal]);

    useEffect(() => {
        setInputComment("");
    }, [props.commentRefreshSignal]);

    const debouncedSendComment = debounce(sendComment, HALF_SECOND_IN_MS);
    function handleSendComment() {
        setIsSubmitting(true);
        debouncedSendComment();
    }

    async function sendComment() {
        try {
            await props.onSubmit(inputComment);
        } finally {
            setIsSubmitting(false);
        }
    }

    const footerElement = (
        <>
            <Input
                onChange={(event) => setInputComment(event.detail.value)}
                value={inputComment}
                disabled={isLoading}
                data-testid="line-comment-input"
            />
            <Box float="right" padding="xxs">
                <SpaceBetween direction="horizontal" size="xs">
                    <Button variant="link" onClick={onDismiss}>
                        {getString(`${stringPath}.cancelButton`)}
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSendComment}
                        loading={isLoading}
                        disabled={isLoading || !inputComment}
                        data-testid="line-send-comment"
                    >
                        {getString(`${stringPath}.sendButton`)}
                    </Button>
                </SpaceBetween>
            </Box>
        </>
    );

    function renderHeader() {
        if (props.showComments) {
            if (
                props.items[0].itemInformation &&
                typeof props.items[0].itemInformation === "object" &&
                "name" in props.items[0].itemInformation
            ) {
                return (
                    <Header variant="h2">
                        {getString(`${stringPath}.header`) + ": " + props.items[0].itemInformation.name}
                    </Header>
                );
            }
        }
        return <Header variant="h2">{getString(`${stringPath}.header`)}</Header>;
    }

    return (
        <Modal
            data-testid="line-comment-modal"
            onDismiss={onDismiss}
            visible={showModal}
            header={renderHeader()}
            footer={footerElement}
            size="large"
        >
            {props.showComments==true ?
                <SpaceBetween size="xs">
                    <SpaceBetween size="xl">
                        <MessageList comments={comments}/>
                    </SpaceBetween>
                </SpaceBetween>
                : null
            }
        </Modal>
    );
};
